import { useMemo } from 'react';
import PropTypes from 'prop-types';

import useDetailTable from '#/hooks/table/useDetailTable';
import { Button } from '#/modules/commonComponents/button';
import { AppDialog } from '#/modules/commonComponents/dialog';
import {
  dialogContentCompactGapClassName,
  dialogWideMaxWidthClassName,
} from '#/modules/commonComponents/dialog/dialogLayoutTokens';
import { DialogFooter } from '#/modules/commonComponents/dialog/DialogPrimitives';
import DetailTable from '#/modules/commonComponents/table/DetailTable';
import { displayOrDash } from '#/modules/crmMgmt/customerOrder/helper/mapCustomerOrderMeta';
import { formatDisplayQty } from '#/utils/formatNumber';

function buildPreviewLineColumns() {
  return [
    {
      id: 'productName',
      header: '產品名稱',
      accessorFn: (row) => displayOrDash(row.productName),
      size: 180,
      enableSorting: false,
      meta: { cellClassName: 'td_left' },
    },
    {
      id: 'productCode',
      header: '產品代號',
      accessorFn: (row) => displayOrDash(row.productCode),
      size: 140,
      enableSorting: false,
      meta: { cellClassName: 'td_left' },
    },
    {
      id: 'qty',
      header: '數量',
      accessorFn: (row) => formatDisplayQty(row.qty),
      size: 90,
      enableSorting: false,
    },
    {
      id: 'remark',
      header: '備註',
      accessorFn: (row) => displayOrDash(row.remark),
      size: 180,
      enableSorting: false,
      meta: { cellClassName: 'td_left' },
    },
  ];
}

/**
 * CO 詳情嵌套銷貨單「預覽產品」：AppDialog + DetailTable。
 * 不嵌 MainInfoCard、不做審核、不 GET 銷貨單 detail。
 * 列上來的 previewLines 由 `salesOrders[].lines` mapper 轉入。
 */
const CustomerOrderSalesOrderPreviewDialog = ({
  open,
  onOpenChange,
  salesOrder,
}) => {
  const columns = useMemo(() => buildPreviewLineColumns(), []);
  const rows = useMemo(
    () => (salesOrder?.previewLines ?? []).map((line, index) => ({
      ...line,
      id: line?.id ?? `preview-line-${index}`,
    })),
    [salesOrder],
  );

  const detailTableState = useDetailTable(rows, columns, {
    enableExpanding: false,
    getRowId: (row) => String(row.id),
  });

  const salesOrderNo = displayOrDash(salesOrder?.salesOrderNo);
  const description = salesOrderNo !== '—'
    ? `銷貨單號 ${salesOrderNo}`
    : undefined;

  const footer = (
    <DialogFooter>
      <Button type="button" variant="outline" onClick={() => onOpenChange(false)}>
        關閉
      </Button>
    </DialogFooter>
  );

  return (
    <AppDialog
      open={open}
      onOpenChange={onOpenChange}
      title="預覽產品"
      description={description}
      headerVariant="primary"
      showCloseButton
      maxWidthClassName={dialogWideMaxWidthClassName}
      contentClassName={dialogContentCompactGapClassName}
      footer={footer}
    >
      {rows.length === 0 ? (
        <p className="py-6 text-sm text-muted-foreground">尚無明細</p>
      ) : (
        <DetailTable
          state={detailTableState}
          containerClassName="max-h-72 max-w-full overflow-auto"
          tableClassName="detail-table--flat-lines"
        />
      )}
    </AppDialog>
  );
};

CustomerOrderSalesOrderPreviewDialog.propTypes = {
  open: PropTypes.bool.isRequired,
  onOpenChange: PropTypes.func.isRequired,
  salesOrder: PropTypes.shape({
    salesOrderNo: PropTypes.string,
    previewLines: PropTypes.arrayOf(PropTypes.object),
  }),
};

export default CustomerOrderSalesOrderPreviewDialog;
